import { buildMultiLegCandidate, type MultiLegDraft } from "./multiLegPosition";
import {
  isWatchOptionEvidenceAuthoringAvailable,
  type WatchOptionEvidenceBaseSubject,
  type WatchOptionEvidenceCapability,
} from "./watchOptionEvidenceCapabilities";
import { buildOccContract } from "./watchOptionContracts";
import {
  findWatchSignal,
  getAuthorableWatchSignals,
  type WatchSignalCatalog,
  type WatchSignalDefinition,
  type WatchSignalOperator,
  type WatchSignalUnit,
} from "./watchSignalCatalog";
import { getReviewedSignalPresenter } from "./watchSignalReleaseRegistry";
import type {
  ExactOptionContractMeasurement,
  MovingAverageTriggerMode,
} from "./watchSignalReleaseRegistry";

const RESET_POLICY = "ALL_FRESH_COMPLETE_NON_TRIGGERING_ONLY" as const;
const DECIMAL = /^(?:0|[1-9]\d*)(?:\.\d+)?$/;

export interface WatchOptionEvidenceUiDraft {
  readonly signalId: string;
  readonly definitionVersion: number;
  readonly direction: "ABOVE" | "BELOW";
  readonly threshold: string;
  /** Required for multi-leg structures and absent for a single option. */
  readonly targetLegIndex?: number;
}

/** Progressive form state; unlike the emitted draft, every choice starts empty. */
export interface WatchOptionEvidenceFormDraft {
  readonly family?: "VOLATILITY" | "LIQUIDITY";
  readonly measurement?: ExactOptionContractMeasurement;
  readonly triggerMode?: MovingAverageTriggerMode;
  readonly signalId?: string;
  readonly definitionVersion?: number;
  readonly direction?: "ABOVE" | "BELOW";
  readonly threshold?: string;
  readonly targetLegIndex?: number;
}

export interface WatchOptionEvidenceAuthoringSpec {
  readonly schema_version: 1;
  readonly base_subject: WatchOptionEvidenceBaseSubject;
  readonly logic: "ALL";
  readonly clauses: readonly [{
    readonly clause_id: "option_condition_1";
    readonly signal_id: string;
    readonly definition_version: number;
    readonly operator: WatchSignalOperator;
    readonly unit: WatchSignalUnit;
    readonly parameters: Readonly<Record<string, string>>;
    readonly binding: null | {
      readonly scope: "OPTION_CONTRACT";
      readonly leg_role: string;
      readonly option_contract: string;
    };
  }];
  readonly reset_policy: typeof RESET_POLICY;
}

function compareUnsignedDecimals(left: string, right: string): number {
  const [leftWhole, leftFraction = ""] = left.split(".");
  const [rightWhole, rightFraction = ""] = right.split(".");
  const scale = Math.max(leftFraction.length, rightFraction.length);
  const leftValue = BigInt(`${leftWhole}${leftFraction.padEnd(scale, "0")}`);
  const rightValue = BigInt(`${rightWhole}${rightFraction.padEnd(scale, "0")}`);
  return leftValue < rightValue ? -1 : leftValue > rightValue ? 1 : 0;
}

function canonicalThreshold(
  definition: WatchSignalDefinition,
  thresholdParameter: string,
  value: string,
): string {
  const descriptor = definition.parameters.find(
    (parameter) => parameter.parameter_id === thresholdParameter,
  );
  if (!descriptor || (descriptor.kind !== "DECIMAL" && descriptor.kind !== "INTEGER")) {
    throw new Error("The selected option signal has no supported threshold contract");
  }
  if (!DECIMAL.test(value) || (descriptor.kind === "INTEGER" && !/^\d+$/.test(value))) {
    throw new Error(descriptor.kind === "INTEGER"
      ? "Enter a canonical whole-number threshold"
      : "Enter a canonical decimal threshold");
  }
  const decimalPlaces = value.includes(".") ? value.length - value.indexOf(".") - 1 : 0;
  if (descriptor.max_decimal_places !== undefined
    && decimalPlaces > descriptor.max_decimal_places) {
    throw new Error("The threshold has too many decimal places");
  }
  if (descriptor.minimum !== undefined
    && compareUnsignedDecimals(value, descriptor.minimum) < 0) {
    throw new Error("The threshold is below the supported minimum");
  }
  if (descriptor.maximum !== undefined
    && compareUnsignedDecimals(value, descriptor.maximum) > 0) {
    throw new Error("The threshold exceeds the supported maximum");
  }
  return value;
}

function canonicalContracts(draft: MultiLegDraft): string[] {
  // This validates the existing supported topology before any clause can bind
  // to a leg. The returned candidate deliberately contains no provider facts.
  buildMultiLegCandidate(draft);
  return draft.legs.map((leg) => buildOccContract({
    ticker: draft.underlying.trim().toUpperCase(),
    contractExpiry: draft.expiry.trim(),
    contractStrike: leg.strike.trim(),
    contractType: leg.optionType,
  }));
}

function canonicalLegRole(draft: MultiLegDraft, targetLegIndex: number): string {
  const leg = draft.legs[targetLegIndex];
  if (!leg) throw new Error("Choose an option leg for this condition");
  if (draft.structure === "single" || draft.structure === "vertical") {
    return `${leg.side.toLowerCase()} ${leg.optionType}`;
  }

  const sortedPuts = draft.legs
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.optionType === "put")
    .sort((left, right) => Number(left.item.strike) - Number(right.item.strike));
  const sortedCalls = draft.legs
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.optionType === "call")
    .sort((left, right) => Number(left.item.strike) - Number(right.item.strike));
  const roles = new Map<number, string>([
    [sortedPuts[0]?.index ?? -1, "long put wing"],
    [sortedPuts[1]?.index ?? -1, "short put"],
    [sortedCalls[0]?.index ?? -1, "short call"],
    [sortedCalls[1]?.index ?? -1, "long call wing"],
  ]);
  const role = roles.get(targetLegIndex);
  if (!role) throw new Error("The selected option leg has no supported topology role");
  return role;
}

/**
 * Build one exact option-evidence clause from customer-entered facts only.
 * This helper grants no authority: capability, catalog, and full presenter
 * review must all agree before it emits the server authoring contract.
 */
export function buildWatchOptionEvidenceSpec(params: {
  capability: WatchOptionEvidenceCapability | null;
  signalCatalog: WatchSignalCatalog | null;
  baseSubject: WatchOptionEvidenceBaseSubject;
  position: MultiLegDraft;
  draft: WatchOptionEvidenceUiDraft;
}): WatchOptionEvidenceAuthoringSpec {
  const { capability, signalCatalog, baseSubject, position, draft } = params;
  if (!isWatchOptionEvidenceAuthoringAvailable(capability, baseSubject)) {
    throw new Error("Option contract conditions are not available");
  }

  const definition = findWatchSignal(
    signalCatalog,
    draft.signalId,
    draft.definitionVersion,
  );
  const job = baseSubject === "POSITION_EXIT" ? "POSITION" : "OPTIONS_ENTRY";
  const authorable = getAuthorableWatchSignals(signalCatalog, {
    job,
    subjectScope: "OPTION_CONTRACT",
  });
  if (!definition || !authorable.includes(definition)) {
    throw new Error("The selected option signal is not authorable");
  }
  const presenter = getReviewedSignalPresenter(definition);
  if (presenter?.kind !== "EXACT_OPTION_CONTRACT") {
    throw new Error("The selected option signal has no reviewed presenter");
  }

  const operator = (draft.direction === "ABOVE"
    ? presenter.triggerMode === "CROSSING" ? "gt" : "gte"
    : presenter.triggerMode === "CROSSING" ? "lt" : "lte") satisfies WatchSignalOperator;
  if (!definition.operators.includes(operator)) {
    throw new Error("The selected direction is not supported by this signal");
  }
  const threshold = canonicalThreshold(
    definition,
    presenter.thresholdParameter,
    draft.threshold,
  );
  const contracts = canonicalContracts(position);

  let binding: WatchOptionEvidenceAuthoringSpec["clauses"][number]["binding"] = null;
  if (position.structure === "single") {
    if (draft.targetLegIndex !== undefined) {
      throw new Error("A single option condition does not accept an explicit leg selection");
    }
  } else {
    if (!Number.isInteger(draft.targetLegIndex)
      || draft.targetLegIndex === undefined
      || draft.targetLegIndex < 0
      || draft.targetLegIndex >= position.legs.length) {
      throw new Error("Choose an option leg for this condition");
    }
    binding = {
      scope: "OPTION_CONTRACT",
      leg_role: canonicalLegRole(position, draft.targetLegIndex),
      option_contract: contracts[draft.targetLegIndex],
    };
  }

  return {
    schema_version: 1,
    base_subject: baseSubject,
    logic: "ALL",
    clauses: [{
      clause_id: "option_condition_1",
      signal_id: definition.signal_id,
      definition_version: definition.definition_version,
      operator,
      unit: definition.unit,
      parameters: {
        direction: draft.direction,
        [presenter.thresholdParameter]: threshold,
      },
      binding,
    }],
    reset_policy: RESET_POLICY,
  };
}
